import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Loader2, 
  DollarSign, 
  ArrowRight, 
  ArrowLeft, 
  User, 
  Building2, 
  CreditCard,
  MapPin,
  CheckCircle,
  Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function SellerSignupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState('');
  
  const [formData, setFormData] = useState({
    // Step 1: Account Info
    email: '',
    password: '',
    confirmPassword: '',
    // Step 2: Business Info
    company_name: '',
    contact_name: '',
    phone: '',
    // Step 3: Payment Info
    payment_method: 'check',
    paypal_email: '',
    zelle_email: '',
    zelle_phone: '',
    // Mailing address for checks
    street_address: '',
    city: '',
    state: '',
    zip_code: ''
  });

  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.company_name) newErrors.company_name = 'Company name is required';
    if (!formData.contact_name) newErrors.contact_name = 'Contact name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (formData.payment_method === 'paypal' && !formData.paypal_email) {
      newErrors.paypal_email = 'PayPal email is required';
    }
    if (formData.payment_method === 'zelle' && !formData.zelle_email && !formData.zelle_phone) {
      newErrors.zelle_email = 'Zelle email or phone is required';
    }
    if (formData.payment_method === 'check') {
      if (!formData.street_address) newErrors.street_address = 'Street address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.state) newErrors.state = 'State is required';
      if (!formData.zip_code) newErrors.zip_code = 'ZIP code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep3()) return;
    
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/seller/auth/signup`, formData);
      
      setSuccess(true);
      setVerificationUrl(response.data.verification_url);
      toast.success('Account created! Check your email to verify.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <Card className="w-full max-w-md relative z-10 bg-black/40 backdrop-blur-xl border-white/10">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
              <Mail className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Check Your Email!</h2>
            <p className="text-gray-400 mb-6">
              We've sent a verification link to <span className="text-green-400">{formData.email}</span>. 
              Click the link to verify your email and activate your account.
            </p>
            
            {/* Dev mode: Show verification link */}
            {verificationUrl && (
              <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm mb-2">Development Mode:</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(verificationUrl)}
                  className="border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/20"
                >
                  Click to Verify Email
                </Button>
              </div>
            )}

            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
                onClick={() => navigate('/seller/login')}
              >
                Go to Login
              </Button>
              <p className="text-gray-500 text-sm">
                Didn't receive the email?{' '}
                <button className="text-green-400 hover:text-green-300">
                  Resend verification
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-lg relative z-10 bg-black/40 backdrop-blur-xl border-white/10">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
            <DollarSign className="w-8 h-8 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl text-white">Become a Lead Seller</CardTitle>
            <CardDescription className="text-gray-400">
              Create your account and start earning
            </CardDescription>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 pt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  s === step 
                    ? 'bg-green-500 text-white' 
                    : s < step 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-white/10 text-gray-500'
                }`}>
                  {s < step ? <CheckCircle className="w-4 h-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-0.5 ${s < step ? 'bg-green-500' : 'bg-white/10'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500 px-4">
            <span>Account</span>
            <span>Business</span>
            <span>Payment</span>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Step 1: Account Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4 text-green-400">
                  <User className="w-5 h-5" />
                  <span className="font-medium">Account Information</span>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && <p className="text-red-400 text-sm">{errors.email}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.password ? 'border-red-500' : ''}`}
                  />
                  {errors.password && <p className="text-red-400 text-sm">{errors.password}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => updateField('confirmPassword', e.target.value)}
                    className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  />
                  {errors.confirmPassword && <p className="text-red-400 text-sm">{errors.confirmPassword}</p>}
                </div>
              </div>
            )}

            {/* Step 2: Business Info */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4 text-green-400">
                  <Building2 className="w-5 h-5" />
                  <span className="font-medium">Business Information</span>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-gray-300">Company Name *</Label>
                  <Input
                    id="company_name"
                    placeholder="Your company or business name"
                    value={formData.company_name}
                    onChange={(e) => updateField('company_name', e.target.value)}
                    className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.company_name ? 'border-red-500' : ''}`}
                  />
                  {errors.company_name && <p className="text-red-400 text-sm">{errors.company_name}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_name" className="text-gray-300">Your Name *</Label>
                  <Input
                    id="contact_name"
                    placeholder="Full name"
                    value={formData.contact_name}
                    onChange={(e) => updateField('contact_name', e.target.value)}
                    className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.contact_name ? 'border-red-500' : ''}`}
                  />
                  {errors.contact_name && <p className="text-red-400 text-sm">{errors.contact_name}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Payment Info */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4 text-green-400">
                  <CreditCard className="w-5 h-5" />
                  <span className="font-medium">Payment Information</span>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Preferred Payment Method *</Label>
                  <Select 
                    value={formData.payment_method} 
                    onValueChange={(value) => updateField('payment_method', value)}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="check">Check (Mailed)</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* PayPal Fields */}
                {formData.payment_method === 'paypal' && (
                  <div className="space-y-2">
                    <Label htmlFor="paypal_email" className="text-gray-300">PayPal Email *</Label>
                    <Input
                      id="paypal_email"
                      type="email"
                      placeholder="paypal@example.com"
                      value={formData.paypal_email}
                      onChange={(e) => updateField('paypal_email', e.target.value)}
                      className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.paypal_email ? 'border-red-500' : ''}`}
                    />
                    {errors.paypal_email && <p className="text-red-400 text-sm">{errors.paypal_email}</p>}
                  </div>
                )}

                {/* Zelle Fields */}
                {formData.payment_method === 'zelle' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="zelle_email" className="text-gray-300">Zelle Email</Label>
                      <Input
                        id="zelle_email"
                        type="email"
                        placeholder="zelle@example.com"
                        value={formData.zelle_email}
                        onChange={(e) => updateField('zelle_email', e.target.value)}
                        className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.zelle_email ? 'border-red-500' : ''}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zelle_phone" className="text-gray-300">Or Zelle Phone</Label>
                      <Input
                        id="zelle_phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.zelle_phone}
                        onChange={(e) => updateField('zelle_phone', e.target.value)}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      />
                    </div>
                    {errors.zelle_email && <p className="text-red-400 text-sm">{errors.zelle_email}</p>}
                  </>
                )}

                {/* Check/Mailing Address Fields */}
                {formData.payment_method === 'check' && (
                  <div className="space-y-4 pt-2">
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <MapPin className="w-4 h-4" />
                      <span>Mailing Address for Checks</span>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="street_address" className="text-gray-300">Street Address *</Label>
                      <Input
                        id="street_address"
                        placeholder="123 Main St"
                        value={formData.street_address}
                        onChange={(e) => updateField('street_address', e.target.value)}
                        className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.street_address ? 'border-red-500' : ''}`}
                      />
                      {errors.street_address && <p className="text-red-400 text-sm">{errors.street_address}</p>}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city" className="text-gray-300">City *</Label>
                        <Input
                          id="city"
                          placeholder="City"
                          value={formData.city}
                          onChange={(e) => updateField('city', e.target.value)}
                          className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.city ? 'border-red-500' : ''}`}
                        />
                        {errors.city && <p className="text-red-400 text-sm">{errors.city}</p>}
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="state" className="text-gray-300">State *</Label>
                        <Select 
                          value={formData.state} 
                          onValueChange={(value) => updateField('state', value)}
                        >
                          <SelectTrigger className={`bg-white/5 border-white/10 text-white ${errors.state ? 'border-red-500' : ''}`}>
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {US_STATES.map(state => (
                              <SelectItem key={state} value={state}>{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.state && <p className="text-red-400 text-sm">{errors.state}</p>}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="zip_code" className="text-gray-300">ZIP Code *</Label>
                      <Input
                        id="zip_code"
                        placeholder="12345"
                        value={formData.zip_code}
                        onChange={(e) => updateField('zip_code', e.target.value)}
                        className={`bg-white/5 border-white/10 text-white placeholder:text-gray-500 ${errors.zip_code ? 'border-red-500' : ''}`}
                      />
                      {errors.zip_code && <p className="text-red-400 text-sm">{errors.zip_code}</p>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-4 border-t border-white/10">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={prevStep}
                  className="text-gray-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <a href="/sell-leads" className="text-gray-400 hover:text-white text-sm">
                  ← Back to info
                </a>
              )}
              
              {step < 3 ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Create Account
                </Button>
              )}
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Already have an account?{' '}
              <a href="/seller/login" className="text-green-400 hover:text-green-300">
                Sign in
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
